import { Entry, ReferencedEntry } from 'contentful';
import sortBy from 'lodash/sortBy';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { FeeType, SalesChannel, SalesChannelPricing } from 'src/api/contentful/contentTypes.shop';
import { IntercomWrapper } from 'src/services/intercom';
import { eraseCookie, readCookie } from 'src/utils/Cookies';
import { getField, isEntry } from 'src/utils/EntryUtil';

import { usePartnerAttribution } from 'src/services/analytics/PartnerAttribution';
import { useBankPersonalization } from '../BankPersonalization/BankPersonalization';
import { BANK_PERSONALIZATION_COOKIE_NAME } from '../BankPersonalization/UseBankPersonalization';
import { REFERRAL_ID_COOKIE_NAME, fetchSalesChannel, getPartnerKey, setStorageData } from './Util';

export const useSalesChannelInternal = (locale: string) => {
  const [ trackingHost, setTrackingHost ] = useState(getPartnerKey());
  const [ salesChannel, setSalesChannel ] = useState<Entry<SalesChannel> | undefined>();
  const { setBankPersonalization } = useBankPersonalization();
  const { setResellerId } = usePartnerAttribution();

  useEffect(() => {
    if (!trackingHost) return;

    const referralId = readCookie(REFERRAL_ID_COOKIE_NAME);

    fetchSalesChannel(locale, trackingHost)
      .then(async item => {
        if (!isEntry(item)) return;
        // If the reseller is different, delete any data that is associated with the old reseller
        if (referralId && getField(item, 'referralId') !== referralId) {
          eraseCookie(BANK_PERSONALIZATION_COOKIE_NAME);
          setBankPersonalization(undefined);
        }

        setSalesChannel(item);
      });
  }, [ trackingHost, locale ]);

  useEffect(() => {
    setStorageData(salesChannel);
    setResellerId(getField(salesChannel, 'cloverResellerId'));

    const liveChatEnabled = !salesChannel || !!(getField(salesChannel, 'liveChatType'));

    // TODO: Remove enabledAndShow and disabledAndHide code, no longer necessary if we shut Intercom down
    if (liveChatEnabled) {
      IntercomWrapper.enableAndShow();
      return;
    }
    IntercomWrapper.shutdown();
  }, [ salesChannel ]);

  const { rateSchedule = [], planSchedule = [] } = useMemo(() => {
    const pricingSchedule = getField(salesChannel, 'pricingSchedule', []);
    const rateSchedule = Array<ReferencedEntry<SalesChannelPricing>>();
    const planSchedule = Array<ReferencedEntry<SalesChannelPricing>>();

    pricingSchedule.forEach(pricing => {
      switch (getField(pricing, 'feeType')) {
        case FeeType.TRANSACTION:
          if (getField(pricing, 'rate') === undefined) break;
          rateSchedule.push(pricing);
          break;
        case FeeType.RECURRING:
          if (getField(pricing, 'price') === undefined) break;
          planSchedule.push(pricing);
          break;
      }
    });

    return {
      rateSchedule: sortBy(rateSchedule, pricing => getField(pricing, 'rate')),
      planSchedule: sortBy(planSchedule, pricing => getField(pricing, 'price')),
    };
  }, [ salesChannel ]);

  const isDisallowedByReseller = useCallback((entry?: ReferencedEntry<any>) => {

    const disallowedResellers = getField(entry, 'disallowedResellers');

    if (!disallowedResellers) return false;

    const referralId = getField(salesChannel, 'referralId');

    if (referralId && disallowedResellers?.includes(referralId)) return true;

    return false;
  }, [salesChannel]);

  return {
    setTrackingHost,
    salesChannel,
    trackingHost,
    rateSchedule,
    planSchedule,
    isDisallowedByReseller,
  };
};

export type SalesChannelContext = ReturnType<typeof useSalesChannelInternal>;
